import { Link, useSearchParams } from "react-router-dom";
import { Ban, Eye, Lock, Mail, ScanEye, ShieldCheck, Sparkles, UserRound, Wand2 } from "lucide-react";
import { BUSINESS } from "@/components/Footer";
import { currentLang, legalT } from "@/i18n";
import { TranslationNotice } from "./LegalPage";

const VARS = { product: BUSINESS.product, name: BUSINESS.name, abn: BUSINESS.abn, location: BUSINESS.location };

const Step = ({ n, icon: Icon, title, children, tint, label }) => (
  <div className="card-soft p-6 flex gap-4">
    <div className="shrink-0 flex flex-col items-center gap-2">
      <span className={`w-12 h-12 rounded-2xl ${tint} border-2 border-[#1E293B] flex items-center justify-center`}><Icon className="w-6 h-6" /></span>
      <span className="text-xs font-bold text-slate-400">{label}</span>
    </div>
    <div>
      <h3 className="font-display text-xl font-semibold mb-1">{title}</h3>
      <div className="text-slate-700 leading-relaxed space-y-2">{children}</div>
    </div>
  </div>
);

const STEPS = [
  { icon: Lock, tint: "bg-[#FFD166]", ps: 2 },
  { icon: Wand2, tint: "bg-[#4ECDC4]", ps: 1 },
  { icon: ScanEye, tint: "bg-[#FF9FF3]", ps: 1 },
  { icon: Eye, tint: "bg-[#1DD1A1]", ps: 1 },
];
const PROMISES = [Ban, UserRound, ShieldCheck];

export default function SafetyPage() {
  const [search] = useSearchParams();
  const lang = currentLang();
  const forceEn = lang !== "en" && search.get("lang") === "en";
  const tt = legalT(forceEn);
  const q = forceEn ? "?lang=en" : "";
  const about = tt("safety_about_p1", VARS);
  const refundTitle = tt("refund_policy");
  const idx = about.toLowerCase().indexOf(refundTitle.toLowerCase());
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 space-y-14" data-testid="safety-page" dir={forceEn ? "ltr" : undefined} lang={forceEn ? "en" : undefined}>
      <header className="max-w-3xl space-y-4 rise">
        <p className="text-sm font-bold uppercase tracking-wider text-[#FF5252]">{tt("safety_kicker", VARS)}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">{tt("safety_h1")}</h1>
        <p className="text-base md:text-lg text-slate-600">{tt("safety_intro", VARS)}</p>
        <TranslationNotice forceEn={forceEn} lang={lang} />
      </header>

      <section className="space-y-5" data-testid="safety-steps">
        <h2 className="text-base md:text-lg font-bold uppercase tracking-wider text-slate-500">{tt("safety_system_title")}</h2>
        {STEPS.map((s, i) => (
          <Step key={i} n={i + 1} icon={s.icon} tint={s.tint} label={tt("safety_step", { n: i + 1 })} title={tt(`safety_step${i + 1}_title`)}>
            {Array.from({ length: s.ps }, (_, j) => <p key={j}>{tt(`safety_step${i + 1}_p${j + 1}`)}</p>)}
          </Step>
        ))}
      </section>

      <section className="grid md:grid-cols-3 gap-5" data-testid="safety-promises">
        {PROMISES.map((Icon, i) => (
          <div key={i} className="card-soft p-6 space-y-2">
            <Icon className="w-8 h-8 text-emerald-600" />
            <h3 className="font-display text-lg font-semibold">{tt(`safety_promise${i + 1}_title`)}</h3>
            <p className="text-slate-600 text-sm leading-relaxed">{tt(`safety_promise${i + 1}_text`, VARS)}</p>
          </div>
        ))}
      </section>

      <section className="card-soft p-8 sm:p-10 grid md:grid-cols-2 gap-8 items-center" data-testid="safety-about">
        <div className="space-y-3">
          <h2 className="font-display text-2xl font-semibold">{tt("safety_about_title", VARS)}</h2>
          <p className="text-slate-700 leading-relaxed">
            {idx < 0 ? about : <>{about.slice(0, idx)}<Link to={`/refunds${q}`} className="underline">{about.slice(idx, idx + refundTitle.length)}</Link>{about.slice(idx + refundTitle.length)}</>}
          </p>
          <p className="text-slate-700 leading-relaxed">{tt("safety_about_p2")}</p>
          <a href={`mailto:${BUSINESS.email}`} className="btn-pop btn-ink inline-flex !py-2.5 !px-5 text-sm" data-testid="safety-contact-button"><Mail className="w-4 h-4" /> {BUSINESS.email}</a>
        </div>
        <div className="flex flex-col gap-3 text-sm">
          {[["privacy_policy", "/privacy"], ["terms_of_service", "/terms"], ["refund_policy", "/refunds"]].map(([k, to]) => (
            <Link key={to} to={`${to}${q}`} className="flex items-center justify-between rounded-2xl border-2 border-slate-200 px-4 py-3 font-bold hover:border-[#1E293B] transition-colors">{tt(k)} <span aria-hidden>→</span></Link>
          ))}
          <Link to="/signup" className="btn-pop btn-red mt-2 justify-center" data-testid="safety-cta-button"><Sparkles className="w-5 h-5" /> {tt("safety_try_free")}</Link>
        </div>
      </section>
    </div>
  );
}
